import React, { useCallback, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import dayjs from 'dayjs';

import {
  AppButton,
  AppForm,
  AppFormLabel,
  AppRadio,
  AppSelect,
} from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { JA_JP } from '@/components/organisms/RA2NDatePicker';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import {
  rezeptInvoiceSearchSchema,
  type RezeptInvoiceSearchFormValues,
} from '@/schemas/rezeptInvoiceSearchSchema';
import { CollapsibleCard } from '@/components/molecules';
import useAppToast from '@/hooks/useAppToast';
import { convGtJMonth } from '@/utils/dateConverter';

const DATEPICKER_FORMAT = parseFormat(JA_JP.format);
const MONTH_DATEPICKER_FORMAT = parseFormat(JA_JP.formatMonth);

export interface RezeptInvoiceSearchFilterProps {
  initialValues?: Partial<RezeptInvoiceSearchFormValues>;
  massagerOptions?: { value: number; label: string }[];
  onSearch: (values: RezeptInvoiceSearchFormValues) => void;
  loading?: boolean;
  onTreatmentFromYearmonthChange?: (yyyymm: string) => void;
}

const defaultValues = (init?: Partial<RezeptInvoiceSearchFormValues>): RezeptInvoiceSearchFormValues => ({
  searchTermType: 1,
  searchTreatementFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
  searchTreatementTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  searchTreatementOne: dayjs().format('YYYY-MM-DD'),
  massagerId: 0,
  sortRadio: 1,
  ...init,
});

const toWarekiMonth = (value?: string) => {
  const d = dayjs(value);
  return d.isValid() ? convGtJMonth(d) : '';
};

const RezeptInvoiceSearchFilter: React.FC<RezeptInvoiceSearchFilterProps> = ({
  initialValues,
  massagerOptions = [],
  onSearch,
  loading = false,
  onTreatmentFromYearmonthChange,
}) => {
  const toast = useAppToast();

  const { control, handleSubmit, watch, setValue, getValues } = useTrimmedForm<RezeptInvoiceSearchFormValues>({
    resolver: zodResolver(rezeptInvoiceSearchSchema),
    defaultValues: defaultValues(initialValues),
    mode: 'onSubmit',
  });

  const termType = watch('searchTermType');
  const lastTermType = useRef<1 | 2>(termType);

  useEffect(() => {
    if (lastTermType.current === termType) return;
    if (termType === 2) {
      const one = getValues('searchTreatementOne');
      if (one) {
        setValue('searchTreatementFrom', one, { shouldValidate: false });
        setValue('searchTreatementTo', one, { shouldValidate: false });
      }
    } else {
      setValue('searchTreatementFrom', dayjs().startOf('month').format('YYYY-MM-DD'), { shouldValidate: false });
      setValue('searchTreatementTo', dayjs().endOf('month').format('YYYY-MM-DD'), { shouldValidate: false });
    }
    lastTermType.current = termType;
  }, [termType, getValues, setValue]);

  const onChangeMonth = useCallback(
    (raw: string) => {
      if (!raw) {
        setValue('searchTreatementFrom', undefined, { shouldValidate: false });
        setValue('searchTreatementTo', undefined, { shouldValidate: false });
        return;
      }
      const parsed = parseDate(raw, MONTH_DATEPICKER_FORMAT);
      const d = dayjs(parsed);
      if (!d.isValid()) {
        setValue('searchTreatementFrom', undefined, { shouldValidate: false });
        setValue('searchTreatementTo', undefined, { shouldValidate: false });
        return;
      }
      const monthStart = d.startOf('month');
      setValue('searchTermType', 1, { shouldValidate: false });
      setValue('searchTreatementFrom', monthStart.format('YYYY-MM-DD'), { shouldValidate: false });
      setValue('searchTreatementTo', monthStart.endOf('month').format('YYYY-MM-DD'), {
        shouldValidate: false,
      });
      setValue('massagerId', 0, { shouldValidate: false });
      onTreatmentFromYearmonthChange?.(monthStart.format('YYYYMM'));
    },
    [setValue, onTreatmentFromYearmonthChange]
  );

  const onChangeOne = useCallback(
    (raw: string) => {
      if (!raw) {
        setValue('searchTreatementOne', undefined, { shouldValidate: false });
        return;
      }
      const parsed = parseDate(raw, DATEPICKER_FORMAT);
      const d = dayjs(parsed);
      if (!d.isValid()) {
        setValue('searchTreatementOne', undefined, { shouldValidate: false });
        return;
      }
      const ymd = d.format('YYYY-MM-DD');
      setValue('searchTreatementOne', ymd, { shouldValidate: false });
      // When in 日別 mode, mirror the selected date into from/to (A2 parity).
      if (getValues('searchTermType') === 2) {
        setValue('searchTreatementFrom', ymd, { shouldValidate: false });
        setValue('searchTreatementTo', ymd, { shouldValidate: false });
      }
    },
    [setValue, getValues]
  );

  // BD: validate fail → "alert" message. Toast each error in field order
  // (from → to → one) so user sees one toast per issue, dismissable.
  const onError = useCallback(
    (errors: Record<string, { message?: string } | undefined>) => {
      const order = ['searchTreatementFrom', 'searchTreatementTo', 'searchTreatementOne'];
      const seen = new Set<string>();
      for (const key of order) {
        const msg = errors[key]?.message;
        if (msg && !seen.has(msg)) {
          seen.add(msg);
          toast.error(msg);
        }
      }
    },
    [toast]
  );

  return (
    <CollapsibleCard title="[訪問用]領収書・請求書">
      <AppForm layout="vertical" onFinish={handleSubmit(onSearch, onError)}>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <Controller
                name="searchTermType"
                control={control}
                render={({ field }) => (
                  <AppRadio
                    checked={field.value === 1}
                    onChange={() => field.onChange(1)}
                    className="min-w-[100px] !mr-0"
                  >
                    月別
                  </AppRadio>
                )}
              />
              <Controller
                name="searchTreatementFrom"
                control={control}
                render={({ field: f, fieldState }) => (
                  <RA2NDatePicker
                    picker="month"
                    value={toWarekiMonth(f.value)}
                    placeholder="元号00年00月"
                    onChange={onChangeMonth}
                    className="min-w-[160px]"
                    error={!!fieldState.error}
                    disabled={termType !== 1}
                  />
                )}
              />
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <Controller
                name="searchTermType"
                control={control}
                render={({ field }) => (
                  <AppRadio
                    checked={field.value === 2}
                    onChange={() => field.onChange(2)}
                    className="min-w-[100px] !mr-0"
                  >
                    日別
                  </AppRadio>
                )}
              />
              <Controller
                name="searchTreatementOne"
                control={control}
                render={({ field: f, fieldState }) => (
                  <RA2NDatePicker
                    value={f.value ?? ''}
                    placeholder="元号00年00月00日"
                    onChange={onChangeOne}
                    className="min-w-[160px]"
                    error={!!fieldState.error}
                    disabled={termType !== 2}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <AppFormLabel label={'施術師情報'} className="!mb-0 min-w-[100px]" />
            <Controller
              name="massagerId"
              control={control}
              render={({ field: f }) => (
                <AppSelect
                  value={f.value ?? 0}
                  onChange={v => f.onChange(v != null && v !== '' ? Number(v) : 0)}
                  options={[{ value: 0, label: 'すべて' }, ...massagerOptions]}
                  className="w-[400px]"
                  allowClear={false}
                />
              )}
            />
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <AppButton type="primary" htmlType="submit" loading={loading}>
            検索
          </AppButton>
        </div>
      </AppForm>
    </CollapsibleCard>
  );
};

export default RezeptInvoiceSearchFilter;
